import { HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { inject, Injectable } from '@angular/core';
import { CustomerService } from './customer.service';
import { ApiError, BaseService } from './base-service';
import {
  DeleteMinutPassAppointmentInput,
  GetMinutPassAppointmentInput,
  GetMinutPassAvailabilitiesInput,
  MinutPassAllServicesResultAPI,
  MinutPassAppointmentSearchResultAPI,
  MinutPassCreateAppointmentResultApi,
  MinutPassServiceResultApi,
  MinutPassTicketOffice,
  PostMinutPassCreateAppointmentInput,
} from '../models/minutpass/minut-pass-models';
import { DatePipe } from '@angular/common';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { logError } from './helpers/sentry.helper';

@Injectable({
  providedIn: 'root',
})
export class MinutPassService extends BaseService {
  private readonly availableServices: CONFIG_CONTEXT[] = [CONFIG_CONTEXT.KITCHEN, CONFIG_CONTEXT.STORAGE];
  private readonly customerService: CustomerService = inject(CustomerService);

  private readonly _dateFormat = 'yyyy-MM-ddTHH:mm:ss';

  public get baseApiUrl() {
    return environment.configurators2d.baseApi;
  }

  public apiUrl(projectType: CONFIG_CONTEXT | string = undefined) {
    return this.baseApiUrl + this.getUrlPath(projectType) + '/MinutPass/';
  }

  private formatDate(date: Date): string {
    const datePipe = new DatePipe('FR-fr');
    return datePipe.transform(date, this._dateFormat);
  }

  private getUrlPath(projectType: CONFIG_CONTEXT | string = undefined): string {
    // TODO : il faudra faire quelque chose dans le ficher d'environnement
    switch (projectType ?? this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        return 'Kitchen';

      case CONFIG_CONTEXT.STORAGE:
        return 'Storage';

      case 'POLE_AMENAGEMENT':
        return 'Kitchen'; //CACO-627 => Pour la spécificité d'un magasin ayant le pole aménagement et non Cuisine / Rangement,
      // on utilise kitchen et on gère dans le back le changement du ticketBusinessId en "POLE_AMENAGEMENT" si code magasin = 1447
    }
    return '';
  }

  public async getAppointments(
    input: GetMinutPassAppointmentInput,
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<MinutPassAppointmentSearchResultAPI[]> {
    const params = new HttpParams()
      .set('startDate', this.formatDate(input.startDate))
      .set('endDate', this.formatDate(input.endDate));
    try {
      const res = await this.get<MinutPassAppointmentSearchResultAPI[]>(
        this.apiUrl(projectType) + 'GetAppointments',
        params,
        undefined,
        false
      );

      //Il se peut qu'il y ai des retours à la ligne dans les commentaires. On nettoie les commentaires
      const data = res.map((appointment: any) => {
        if (appointment.extra_data?.comment) {
          appointment.extra_data.comment = appointment.extra_data.comment.replace(/[\r\n]+/g, ' ').trim();
        }
        return appointment;
      });

      return data;
    } catch (err) {
      throw err;
    }
  }

  public async getAllAppointments(input: GetMinutPassAppointmentInput): Promise<MinutPassAppointmentSearchResultAPI[]> {
    let appointments: MinutPassAppointmentSearchResultAPI[] = [];
    try {
      for (const service of this.availableServices) {
        const response = await this.getAppointments(input, service);
        appointments.push(...response);
      }
      return appointments;
    } catch (err) {
      throw err;
    }
  }

  public async getVendorAppointmentsCount(startDate: Date, endDate: Date): Promise<number> {
    try {
      const allAppointments = await this.getAllAppointments({
        startDate: startDate,
        endDate: endDate,
      });

      const currentVendorInfo = this.current.vendor;

      if (allAppointments instanceof ApiError) return 0;

      const appointmentsInRange = allAppointments.filter(appointment => {
        const appointmentDate = new Date(appointment.date_time);
        appointmentDate.setHours(0, 0, 0, 0);
        return (
          appointmentDate >= startDate &&
          appointmentDate <= endDate &&
          //recherche par nom dans la case "managed by" car un RDV peut être affecté automatiquement à quelqu'un par minut pass mais pas nécessairement réalisé par lui. seul pb : s'il y a une faute dans son nom
          (currentVendorInfo.name as string).toUpperCase().includes(appointment.extra_data.managed_by.toUpperCase())
        );
      });

      return appointmentsInRange.length;
    } catch (error) {
      logError('Erreur lors du comptage des tâches:', error);
      throw error;
    }
  }

  public async getTodayVendorAppointmentsCount(): Promise<number> {
    let today: Date = new Date();
    today.setHours(0, 0, 0, 0);
    return await this.getVendorAppointmentsCount(today, today);
  }

  public getServices(projectType: CONFIG_CONTEXT | string = undefined): Promise<MinutPassServiceResultApi[]> {
    return this.get<MinutPassServiceResultApi[]>(this.apiUrl(projectType) + 'GetServices');
  }

  private async getAllServices(
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<MinutPassAllServicesResultAPI> {
    const res = await this.get(this.apiUrl(projectType) + 'GetAllServices');
    return { stores: res };
  }

  public async getAllStoreTicketOffices(
    storeId: string,
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<MinutPassTicketOffice[]> {
    try {
      const allServices: MinutPassAllServicesResultAPI = await this.getAllServices(projectType);
      const allCurrentStoreServices = allServices.stores.find(s => s.id === Number(storeId));
      return allCurrentStoreServices.ticket_offices;
    } catch (error) {}
  }

  public getAvailabilites(
    input: GetMinutPassAvailabilitiesInput,
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<MinutPassAppointmentSearchResultAPI[]> {
    const params = new HttpParams()
      .set('startDate', this.formatDate(input.startDate))
      .set('endDate', this.formatDate(input.endDate))
      .set('serviceBusinessId', input.serviceBusinessId);
    return this.get<MinutPassAppointmentSearchResultAPI[]>(this.apiUrl(projectType) + 'GetAvailabilities', params);
  }

  public postAppointment(
    input: PostMinutPassCreateAppointmentInput,
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<MinutPassCreateAppointmentResultApi> {
    //Nettoyer le commentaire s'il y a des /n et des espaces multiples
    if (input.extraData?.comment) {
      input.extraData.comment = input.extraData.comment
        .replace(/[\r\n]+/g, ' ') // Remplacer par des espaces
        .replace(/\s+/g, ' ') // Normaliser les espaces multiples
        .trim();
    }

    return this.post<MinutPassCreateAppointmentResultApi>(this.apiUrl(projectType) + 'CreateAppointment', input);
  }

  public deleteAppointment(
    input: DeleteMinutPassAppointmentInput,
    projectType: CONFIG_CONTEXT | string = undefined
  ): Promise<string> {
    return this.delete<string>(this.apiUrl(projectType) + 'DeleteAppointment', input);
  }
}
